import { useEffect } from 'react'

type Props = {
  title?: string
  description?: string
  keywords?: string
  canonical?: string
  ogTitle?: string
  ogDescription?: string
  ogImage?: string
  twitterCard?: string
  twitterTitle?: string
  twitterDescription?: string
  twitterImage?: string
  noindex?: boolean
  jsonLd?: Record<string, unknown>
}

function upsertMeta(name: string, content: string) {
  if (!content) return
  const el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null
  if (el) el.content = content
  else {
    const m = document.createElement('meta')
    m.setAttribute('name', name)
    m.setAttribute('content', content)
    document.head.appendChild(m)
  }
}

function upsertProperty(property: string, content: string) {
  if (!content) return
  const el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null
  if (el) el.setAttribute('content', content)
  else {
    const m = document.createElement('meta')
    m.setAttribute('property', property)
    m.setAttribute('content', content)
    document.head.appendChild(m)
  }
}

function upsertLink(rel: string, href: string) {
  if (!href) return
  const el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null
  if (el) el.href = href
  else {
    const l = document.createElement('link')
    l.setAttribute('rel', rel)
    l.setAttribute('href', href)
    document.head.appendChild(l)
  }
}

export default function SEO({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  twitterCard,
  twitterTitle,
  twitterDescription,
  twitterImage,
  noindex,
  jsonLd,
}: Props) {
  useEffect(() => {
    if (title) document.title = title
    if (description) upsertMeta('description', description)
    if (keywords) upsertMeta('keywords', keywords)
    upsertLink('canonical', canonical || window.location.href)
    upsertProperty('og:type', 'website')
    upsertProperty('og:url', window.location.href)
    upsertProperty('og:title', ogTitle || title || '')
    upsertProperty('og:description', ogDescription || description || '')
    if (ogImage) upsertProperty('og:image', ogImage)
    upsertMeta('twitter:card', twitterCard || 'summary_large_image')
    upsertMeta('twitter:title', twitterTitle || title || '')
    upsertMeta('twitter:description', twitterDescription || description || '')
    if (twitterImage) upsertMeta('twitter:image', twitterImage)
    const robots = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null
    if (noindex) {
      if (robots) robots.content = 'noindex, nofollow'
      else {
        const m = document.createElement('meta')
        m.setAttribute('name', 'robots')
        m.setAttribute('content', 'noindex, nofollow')
        document.head.appendChild(m)
      }
    } else if (robots) robots.remove()
    const existing = document.getElementById('seo-jsonld') as HTMLScriptElement | null
    if (existing) existing.remove()
    if (jsonLd) {
      const s = document.createElement('script')
      s.id = 'seo-jsonld'
      s.type = 'application/ld+json'
      s.text = JSON.stringify(jsonLd)
      document.head.appendChild(s)
    }
  }, [
    title,
    description,
    keywords,
    canonical,
    ogTitle,
    ogDescription,
    ogImage,
    twitterCard,
    twitterTitle,
    twitterDescription,
    twitterImage,
    noindex,
    jsonLd,
  ])
  return null
}

